import { useDepositWizard } from "features/wallet/components/deposit/hooks/useDepositWizard";
import React, { useEffect, useState } from "react";
import { useCompleteDeposit } from "features/wallet/components/deposit/hooks/useCompleteDeposit";
import {
  DepositContent,
  DepositContentSkeleton,
} from "../../blocks/deposit-content/DepositContent";
import { DepositAmountInput } from "../../blocks/deposit-amount-input/DepositAmountInput";
import { DepositMethodField } from "../../blocks/deposit-method-field/DepositMethodField";
import { SectionHeader } from "../../blocks/section-header/SectionHeader";
import { ApplePayButton } from "features/wallet/components/ApplePayButton";
import { useApplePay } from "features/wallet/hooks/useApplePay";
import { useWalletStore } from "features/wallet/stores/walletStore";
import { useDepositAmountValidationSchema } from "features/wallet/hooks/useDepositAmountValidationSchema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type z from "zod";
import * as styles from "./ApplePayPrimaryView.module.scss";
import { Form, Modal, Skeleton, toast } from "@picklebet/ui";
import { SectionDivider } from "../../blocks/section-divider/SectionDivider";
import { AmountInputSkeleton } from "@/features/wallet/components/shared/amount-input-skeleton/AmountInputSkeleton";
import { MethodRowSkeleton } from "../../method-row/MethodRow";
import { useReportSubmitting } from "features/wallet/hooks/useReportSubmitting";
import { isAppleWebView } from "utilities/display";

export const ApplePayPrimaryViewSkeleton = () => {
  return (
    <DepositContentSkeleton>
      <div className={styles.fields}>
        <AmountInputSkeleton />

        <SectionDivider />

        <SectionHeader>Payment Method</SectionHeader>

        <MethodRowSkeleton />
      </div>

      <Modal.Footer>
        <Skeleton className={styles.buttonSkeleton} />
      </Modal.Footer>
    </DepositContentSkeleton>
  );
};

export const ApplePayPrimaryView = () => {
  const appleDepositStatus = useWalletStore(
    (state) => state.appleDepositStatus,
  );
  const appleDepositError = useWalletStore((state) => state.appleDepositError);
  const setAppleDepositError = useWalletStore(
    (state) => state.setAppleDepositError,
  );
  const setAppleDepositStatus = useWalletStore(
    (state) => state.setAppleDepositStatus,
  );

  const depositWithApplePay = useApplePay();
  const { amount, setAmount, setSubmitting } = useDepositWizard();
  const completeDeposit = useCompleteDeposit();
  const [schema, loading] = useDepositAmountValidationSchema();

  const isAppleApp = isAppleWebView();
  const [sessionActive, setSessionActive] = useState(false);

  const isProcessing = appleDepositStatus === "processing";

  useReportSubmitting(isAppleApp ? isProcessing : sessionActive, setSubmitting);

  useEffect(() => {
    if (appleDepositStatus === "success") {
      setSessionActive(false);
      completeDeposit();
    }
  }, [appleDepositStatus]);

  useEffect(() => () => setAppleDepositStatus("ready"), []);

  const {
    control,
    formState: { errors, isValid },
    watch,
    trigger,
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount,
    },
  });

  const amountFormValue = watch("amount");
  const [amountErrorShown, setAmountErrorShown] = useState(false);

  // this form only validates when clicking the Apple Pay button, trigger
  // validation when the amount changes instead
  useEffect(() => {
    if (amountErrorShown) {
      trigger();
    }
  }, [amountFormValue, amountErrorShown]);

  // consumed as soon as it is raised, so a retry can surface the same message
  useEffect(() => {
    if (!appleDepositError) return;

    toast({
      variant: "danger",
      title: "Deposit Failed",
      description: appleDepositError,
    });

    setAppleDepositError(null);
  }, [appleDepositError]);

  return (
    <Form noSpacing>
      <DepositContent>
        <div className={styles.fields} data-locked={isProcessing}>
          <Controller
            control={control}
            name="amount"
            render={({ field: { onChange, value, ...field } }) => (
              <DepositAmountInput
                value={value}
                onChange={(next) => {
                  onChange(next);
                  setAmount(next);
                }}
                errorMessage={errors.amount?.message}
                {...field}
              />
            )}
          />

          <SectionDivider />

          <SectionHeader>Payment Method</SectionHeader>

          <DepositMethodField />
        </div>

        <Modal.Footer>
          <ApplePayButton
            loading={loading}
            processing={isProcessing}
            isValid={isValid}
            amount={amountFormValue}
            onDeposit={(value) => {
              // only the browser reports its own dismissal, so only it has a
              // session to track
              if (!isAppleApp) {
                setSessionActive(true);
              }

              depositWithApplePay(value, {
                onSuccess: () => {
                  return;
                },
                onDismiss: () => setSessionActive(false),
              });
            }}
            onValidationError={() => setAmountErrorShown(true)}
          />
        </Modal.Footer>
      </DepositContent>
    </Form>
  );
};
